import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import { validateField } from '../../utils/validation';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';
const Services = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canServices = (key) => can(key, 'services');

    // Pagination
    const pagination = usePagination(10);

    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Date filter
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [services, setServices] = useState([]);
    const [formData, setFormData] = useState({ name: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [itemToDelete, setItemToDelete] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    // Fetch services
    const fetchServices = async (searchValue = appliedSearchTerm) => {
        try {
            const response = await api.get(`/md/company-service-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue,
                    from: fromDate,
                    to: toDate
                }
            });
            setServices(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setTotal(response.data.totalElements);
            pagination.setCurrentPageItems(response.data.content.length);
        } catch (error) {
            let errorMessage = 'Failed to fetch services.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view services.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while fetching services.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching services.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
            setServices([]);
        }
    };

    // Update useEffect to include search in dependencies
    useEffect(() => {
        fetchServices();
    }, [pagination.page, pagination.size, appliedSearchTerm, fromDate, toDate]);

    // Open modal for create
    const handleCreate = () => {
    if (!canServices('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '' });
        setEditingId(null);
        setErrors({});
        setShowModal(true);
        const modal = new window.bootstrap.Modal(document.getElementById('modal-service'));
        modal.show();
    };

    // Open modal for edit
    const handleEdit = (service) => {
    if (!canServices('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({ name: service.name });
        setEditingId(service.id);
        setErrors({});
        setShowModal(true);
        const modal = new window.bootstrap.Modal(document.getElementById('modal-service'));
        modal.show();
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateField('name', formData.name, { required: true });
        if (error) {
            setErrors({ name: error });
            return;
        }
        try {
            if (editingId) {
                await api.put(`/md/company-service/${editingId}`, { id: editingId, name: formData.name });
                setSuccessMessage('Service has been successfully updated.');
            } else {
                await api.post('/md/company-service', { name: formData.name });
                setSuccessMessage('Service has been successfully added.');
            }
            setShowSuccessModal(true);
            setShowModal(false);
            setEditingId(null);
            setFormData({ name: '' });
            setErrors({});
            await fetchServices();
            const modal = document.getElementById('modal-service');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            console.error('Error saving service:', error);
            let errorMessage = 'Failed to save service.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        errorMessage = 'A service with this name already exists.';
                        technicalDetails = `Duplicate entry for service name: "${formData.name}"`;
                        break;
                    case 400:
                        errorMessage = 'Invalid service data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'Service not found.';
                        technicalDetails = 'The requested service may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the service.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Edit selected (only works for single selection)
    const editSelected = () => {
        if (selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one service to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = selectedIds[0];
        const selectedService = services.find(service => service.id === selectedId);
        if (selectedService) {
            handleEdit(selectedService);
        }
    };

    // Delete selected
    const deleteSelected = () => {
    if (!canServices('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one service to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await api.delete('/md/company-service/bulk', { data: selectedIds });
                setSelectedIds([]);
                fetchServices();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected services deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                console.error('Error deleting services:', error);
                let errorMessage = 'Failed to delete services.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these services.';
                            technicalDetails = 'Required role or permission is missing for bulk deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails = error.response.data.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more services not found.';
                            technicalDetails = 'Some services may have been already deleted';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting services.';
                            technicalDetails = error.response.data.message || 'Internal server error during bulk deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the services.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    // Modal cleanup
    useEffect(() => {
        const modal = document.getElementById('modal-service');
        modal?.addEventListener('hidden.bs.modal', () => {
            setFormData({ name: '' });
            setEditingId(null);
        });
        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        // Real-time validation
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({
            ...prev,
            [name]: error
        }));
    };

    return (
        <>
                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title d-flex align-items-center">
                                    <a className="me-2 d-flex align-items-center" href="./masterdata-main.html">
                                        <span className="d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                strokeLinecap="round" strokeLinejoin="round"
                                                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M15 6l-6 6l6 6" />
                                            </svg>
                                        </span>
                                    </a>
                                    Services
                                </h2>
                            </div>
                            <div className="col-auto ms-auto d-print-none">
                                <div className="btn-list">
                                    <a href="#" className="btn btn-primary btn-5 d-none d-sm-inline-block"
                                        onClick={handleCreate}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                        Create
                                    </a>
                                    <a href="#" className="btn btn-primary btn-6 d-sm-none btn-icon" onClick={handleCreate} aria-label="Create new report">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <GenericView
                                        data={services}
                                        columns={[
                                            { key: 'name', label: 'Name', sortable: true },
                                            { key: 'cuser', label: 'Created By', sortable: true, render: (value, row) => row.cuser?.username || '-' },
                                            { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
                                        ]}
                                        isLoading={capabilitiesLoading}
                                        emptyStateMessage="No services found"
                                        checkboxSelection={true}
                                        selectedRows={selectedIds}
                                        onSelectRow={(itemId, isSelected) => {
                                            if (isSelected) {
                                                setSelectedIds([...selectedIds, itemId]);
                                            } else {
                                                setSelectedIds(selectedIds.filter(id => id !== itemId));
                                            }
                                        }}
                                        onSelectAllRows={(itemIds) => {
                                            if (Array.isArray(itemIds) && itemIds.length > 0) {
                                                setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                            } else {
                                                setSelectedIds([]);
                                            }
                                        }}
                                        onEdit={canServices('edit') ? (item) => {
                                            setSelectedIds([item.id]);
                                            handleEdit(item);
                                        } : undefined}
                                        onDelete={canServices('delete') ? (item) => {
                                            setSelectedIds([item.id]);
                                            deleteSelected();
                                        } : undefined}
                                        searchComponent={
                                            <div className="d-flex gap-2">
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={handleSearchInputChange}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSearchSubmit();
                                                        }
                                                    }}
                                                    placeholder="Search..."
                                                    uniqueId="services-search"
                                                />
                                                <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                                                    Search
                                                </button>
                                            </div>
                                        }
                                        actionButtons={
                                            <div className="d-flex gap-2">
                                                {(canServices('edit') || canServices('delete')) && (
                                                    <div ref={actionsContainerRef} className="dropdown">
                                                        <a
                                                            ref={actionsToggleRef}
                                                            href="#"
                                                            className="btn dropdown-toggle"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded={isActionsOpen}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const inst = actionsDropdownInstanceRef.current;
                                                                if (inst?.toggle) inst.toggle();
                                                                else setIsActionsOpen(prev => !prev);
                                                            }}
                                                        >Actions</a>
                                                        <div className="dropdown-menu">
                                                            {canServices('edit') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                                    style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                    Edit
                                                                </a>
                                                            )}
                                                            {canServices('delete') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                    style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                    Delete
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        pagination={{
                                            currentPage: pagination.page + 1,
                                            totalRows: pagination.total,
                                            rowsPerPage: pagination.size,
                                            onPageChange: (page) => pagination.setPage(page - 1),
                                            onRowsPerPageChange: (size) => {
                                                pagination.setSize(size);
                                                pagination.setPage(0);
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        uniqueId="services-table"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            
            {/* Modal for add/edit service */}
            <div className="modal modal-blur fade" id="modal-service" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Service' : 'Add New Service'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        name="name"
                                        placeholder="Enter service name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary btn-3"
                                    data-bs-dismiss="modal">
                                    Cancel
                                </a>
                                <a href="#" className="btn btn-primary ms-auto" id="createBtn" onClick={handleSubmit}>
                                    {editingId ? 'Update' : 'Save'}
                                </a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={selectedIds.length > 0
                    ? "Are you sure you want to delete the selected services? This action cannot be undone."
                    : "Are you sure you want to delete this service? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Operation Failed"
                message={failMessage}
                buttonText="Close"
            />
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
        </>
    );
};

export default Services;

